import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = authenticated
  const [user, setUser] = useState(null);

  // Auth lives in an httpOnly cookie — bootstrap by asking /auth/me directly.
  // If the cookie is missing/expired the server returns 401 → user becomes `false`.
  const bootstrap = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // 401 (no/expired cookie) is the common case — silent.
      if (e?.response?.status !== 401) {
        console.warn("Auth bootstrap failed", e);
      }
      setUser(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e?.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Server-side logout failed (network or already-expired) — proceed with local logout.
      console.warn("Logout: server call failed", e);
    }
    setUser(false);
  };

  const refreshMe = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      console.warn("refreshMe failed", e);
    }
  };

  return (
    <AuthContext.Provider value={useMemo(() => ({ user, login, logout, refreshMe }), [user])}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
